import { ComponentChildren, Ref } from 'preact';
import { useEffect, useRef, useState, useCallback, useMemo, useContext } from 'preact/hooks';
import { BaseButton } from '../UNSAFE_BaseButton';
import { ButtonLabelLayout } from '../UNSAFE_ButtonLabelLayout';
import { ChevronLeft } from '../UNSAFE_RedwoodIcons/ChevronLeft';
import { ChevronRight } from '../UNSAFE_RedwoodIcons/ChevronRight';
import { ChevronUp } from '../UNSAFE_RedwoodIcons/ChevronUp';
import { ChevronDown } from '../UNSAFE_RedwoodIcons/ChevronDown';
import { classNames as clsx } from '../utils/UNSAFE_classNames';
import { styles } from './themes/ConveyorBeltStyles.css';
import { useUser } from '../hooks/UNSAFE_useUser';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { ConveyorBeltContext } from './ConveyorBeltContext';
import { ConveyorBeltFocusContext } from './ConveyorBeltFocusContext';
import { TestIdProps, useTestId } from '../hooks/UNSAFE_useTestId';
import { forwardRef, useImperativeHandle } from 'preact/compat';
import { ConveyorBeltVariantOptions } from './themes/ConveyorBeltStyles.css';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { ConveyorBeltRedwoodTheme } from './themes/redwood/ConveyorBeltTheme';

export type ConveyorBeltProps = TestIdProps & {
  /**
   * ConveyorBelt Component content
   */
  children?: ComponentChildren;
  /**
   * Sets the number of pixels that an element's content is scrolled from its initial position.
   */
  scrollPosition?: number;
  /**
   * Callback that is executed every time conveyor belt is scrolled and the scroll position is changed.
   * @param value The value is the new scroll position of the conveyor belt.
   * @returns
   */
  onScrollPositionChanged?: (value?: number) => void;
  /**
   * Indicates whether overflow content arrows are visible or hidden.
   * "auto" show overflow arrows on desktop, hide on mobile.
   * "visible" always show overflow arrows.
   * "hidden" never show overflow arrows.
   */
  arrowVisibility?: 'auto' | 'visible' | 'hidden';

  /**
   * Specify the orientation of the conveyorBelt.
   * "horizontal" Orient the conveyorBelt horizontally.
   * "vertical" Orient the conveyorBelt vertically.
   */
  orientation?: 'horizontal' | 'vertical';
};

/**
 * Helper function to determine whether the current device is a mobile device
 * @returns true if runnning on a mobile device, false otherwise
 */
function isMobile() {
  const deviceType = getClientHints().deviceRenderMode;
  return deviceType === 'phone';
}

function getOffsetParent(element: HTMLElement) {
  if (getComputedStyle(element).position === 'fixed') {
    return null;
  }

  return element.offsetParent as HTMLElement;
}

function getOffsetLeft(element: HTMLElement, contentContainer?: HTMLElement | null) {
  let offsetParent = getOffsetParent(element);
  let offsetLeft = element.offsetLeft;

  while (offsetParent && offsetParent !== contentContainer && offsetParent !== document.body) {
    offsetLeft = offsetLeft + offsetParent.offsetLeft;
    offsetParent = getOffsetParent(offsetParent);
  }
  return offsetLeft;
}

function getOffsetTop(element: HTMLElement, contentContainer?: HTMLElement | null) {
  let offsetParent = getOffsetParent(element);
  let offsetTop = element.offsetTop;

  while (offsetParent !== contentContainer) {
    if (offsetParent != null) {
      offsetTop = offsetTop + offsetParent.offsetTop;
      offsetParent = getOffsetParent(offsetParent);
    }
  }
  return offsetTop;
}

type ConveyorItemElement = {
  element: HTMLElement;
  isVisible: boolean;
  index: number;
};

// utility hook that calculates which conveyorbelt items are visible inside conveyorbelt viewport
// and which are hidden
const useConveyorElementsVisible = (
  orientation: 'horizontal' | 'vertical',
  root?: HTMLDivElement,
  prevBtn?: HTMLDivElement,
  nextBtn?: HTMLDivElement,
  direction?: 'ltr' | 'rtl'
) => {
  const observerRef = useRef<IntersectionObserver>();
  const targetsRef = useRef<ConveyorItemElement[]>([]);
  const [targets] = useState<ConveyorItemElement[]>([]);
  const addTarget = useCallback(
    (target: ConveyorItemElement) => {
      if (targetsRef.current !== null) {
        targetsRef.current.push(target);
        targets.push(target);
      }
    },
    [targets]
  );

  useEffect(() => {
    if (targets && targets.length > 0 && root) {
      // IntersectionObserver calls visibility change only when item is
      // fully visible inside (threshold: 1)
      // conveyorbelt viewport (root),
      // taking arrow buttons into the account
      // and viewport should be smaller by the buttons width (rootMargin)
      const rootMargin =
        orientation === 'horizontal'
          ? direction === 'ltr'
            ? `0px ${nextBtn ? -nextBtn.offsetWidth : 0}px 0px ${
                prevBtn ? -prevBtn.offsetWidth : 0
              }px`
            : `0px ${prevBtn ? -prevBtn.offsetWidth : 0}px 0px ${
                nextBtn ? -nextBtn.offsetWidth : 0
              }px`
          : `${nextBtn ? -nextBtn.offsetHeight : 0}px 0px ${
              prevBtn ? -prevBtn.offsetHeight : 0
            }px 0px`;

      observerRef.current = new IntersectionObserver(onVisibilityChange, {
        root,
        rootMargin: rootMargin,
        threshold: 1
      });
      for (const target of targets) {
        if (target != null) observerRef.current.observe(target.element);
      }
    }
    return () => {
      for (const target of targets) {
        if (observerRef.current && target != null) observerRef.current.unobserve(target.element);
      }
    };
  }, [targets, targets.length, root, nextBtn, prevBtn, direction, orientation]);

  // handle visibility changes
  const onVisibilityChange = (entries: IntersectionObserverEntry[]) => {
    const newItems: ConveyorItemElement[] = targetsRef.current.map((conveyorItem, index) => {
      const foundEntry = entries.find((entry) => entry.target === conveyorItem.element);
      // if visibility changed for the item, change isVisible property,
      // otherwise just return the original
      if (foundEntry) {
        return {
          element: foundEntry.target as HTMLDivElement,
          isVisible: foundEntry.isIntersecting,
          index: index
        };
      } else {
        return conveyorItem;
      }
    });
    targetsRef.current = newItems;
  };

  return useMemo(
    () => ({
      itemElementsRef: targetsRef,
      addItem: addTarget
    }),
    [targetsRef, addTarget]
  );
};

export type ScrollableHandle = {
  scrollElementIntoView: (element: HTMLElement) => void;
};

/**
 * The Conveyor belt component is a container element that manages
 * overflow for its child elements and allows scrolling among them
 */
export const ConveyorBelt = forwardRef(
  (
    {
      children,
      scrollPosition,
      onScrollPositionChanged,
      arrowVisibility = 'auto',
      orientation = 'horizontal',
      testId
    }: ConveyorBeltProps,
    ref: Ref<ScrollableHandle> = null
  ) => {
    const [canPaginateNext, setCanPaginateNext] = useState(false);
    const [canPaginatePrevious, setCanPaginatePrevious] = useState(false);
    const overflowContainerRef = useRef<HTMLDivElement | null>(null);
    const contentContainerRef = useRef<HTMLDivElement | null>(null);
    const nextButtonContainerRef = useRef<HTMLDivElement | null>(null);
    const prevButtonContainerRef = useRef<HTMLDivElement | null>(null);
    const baseRef = useRef<HTMLDivElement | null>(null);
    const leftRef = useRef<HTMLDivElement | null>(null);
    const rightRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const currentItemRef = useRef<HTMLElement | null>(null);
    const [buttonsHidden, setButtonsHidden] = useState(
      (isMobile() && arrowVisibility === 'auto') || arrowVisibility === 'hidden'
    );
    const { direction } = useUser();
    const { itemElementsRef, addItem } = useConveyorElementsVisible(
      orientation,
      overflowContainerRef.current ?? undefined,
      prevButtonContainerRef.current ?? undefined,
      nextButtonContainerRef.current ?? undefined,
      direction
    );
    const [activeItemId, setActiveItemId] = useState<string | undefined>();

    // Add state to track button readiness and a pending scroll ref
    const [buttonsReady, setButtonsReady] = useState(false);
    const pendingScrollRef = useRef<HTMLElement | null>(null);

    useImperativeHandle(ref, () => ({
      scrollElementIntoView: (element: HTMLElement) => {
        scrollIntoView(element);
      },
      scrollPrevious: () => {
        scrollToPreviousPage();
      },
      scrollNext: () => {
        scrollToNextPage();
      }
    }));

    const testIdProps = useTestId(testId);
    const leftArrowTestIdProps = 'leftArrow' + testId;
    const rightArrowTestIdProps = 'rightArrow' + testId;

    const { variantClasses } = useComponentTheme<ConveyorBeltVariantOptions>(
      ConveyorBeltRedwoodTheme,
      {
        orientation
      }
    );

    const { variantClasses: contentClasses } = useComponentTheme<ConveyorBeltVariantOptions>(
      ConveyorBeltRedwoodTheme,
      {
        content: orientation
      }
    );

    const { variantClasses: overflowClasses } = useComponentTheme<ConveyorBeltVariantOptions>(
      ConveyorBeltRedwoodTheme,
      {
        pagination:
          canPaginateNext && canPaginatePrevious
            ? 'both'
            : canPaginateNext
            ? 'next'
            : canPaginatePrevious
            ? 'previous'
            : 'none',
        direction,
        orientation,
        overflow: orientation,
        arrowVisibility: buttonsHidden === true ? 'hidden' : 'visible'
      }
    );

    const { classes: nextButtonClasses } = useComponentTheme<ConveyorBeltVariantOptions>(
      ConveyorBeltRedwoodTheme,
      {
        nextButton: orientation,
        direction
      }
    );

    const { classes: previousButtonClasses } = useComponentTheme<ConveyorBeltVariantOptions>(
      ConveyorBeltRedwoodTheme,
      {
        previousButton: orientation,
        direction
      }
    );

    // utility method to get all conveyorbelt items
    const _getConveyorItems = useCallback(() => {
      const conveyorContentChildren = contentContainerRef.current
        ? Array.from(contentContainerRef.current.querySelectorAll('[data-oj-conveyorbelt-item]'))
        : [];

      if (conveyorContentChildren) {
        let index = -1;
        itemElementsRef.current = [];
        for (const child of conveyorContentChildren) {
          index++;
          const element = child as HTMLElement;
          if (element) {
            addItem({
              element: element,
              isVisible: false,
              index: index
            });
          }
        }
      }
    }, [contentContainerRef, itemElementsRef, addItem]);

    useEffect(() => {
      _getConveyorItems();
    }, [children, _getConveyorItems]);

    const scrollEndHandler = useCallback(() => {
      if (onScrollPositionChanged)
        onScrollPositionChanged(overflowContainerRef.current?.scrollLeft);
    }, [onScrollPositionChanged]);

    useEffect(() => {
      setButtonsHidden((isMobile() && arrowVisibility === 'auto') || arrowVisibility === 'hidden');
    }, [arrowVisibility]);

    useEffect(() => {
      if (!overflowContainerRef.current) return;
      overflowContainerRef.current.scrollTo({
        left: scrollPosition
      });
    }, [scrollPosition]);

    // useEffect hook that calculates when arrow buttons should become visible or hidden
    // using IntersectionObserver
    useEffect(() => {
      const overflowContainer = overflowContainerRef.current;
      const leftGuard = leftRef.current;
      const rightGuard = rightRef.current;

      if (overflowContainer && leftGuard && rightGuard) {
        if (!buttonsHidden) {
          observerRef.current = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.target.getAttribute('data-intersection-id') === 'left') {
                  setCanPaginatePrevious(!entry.isIntersecting);
                } else {
                  setCanPaginateNext(!entry.isIntersecting);
                }
              });
            },
            {
              root: overflowContainer
            }
          );
          observerRef.current.observe(leftGuard);
          observerRef.current.observe(rightGuard);
        }
        overflowContainer.addEventListener('scrollend', scrollEndHandler);
      }

      // Clean-up
      return () => {
        if (!overflowContainer) return;
        overflowContainer.removeEventListener('scrollend', scrollEndHandler);
        observerRef.current?.disconnect();
      };
    }, [buttonsHidden, scrollEndHandler]);

    const scrollIntoView = useCallback(
      (element: HTMLElement) => {
        const prevOffsetWidth = prevButtonContainerRef.current
          ? prevButtonContainerRef.current.offsetWidth
          : 0;
        const prevOffsetHeight = prevButtonContainerRef.current
          ? prevButtonContainerRef.current.offsetHeight
          : 0;
        const nextOffsetWidth = nextButtonContainerRef.current
          ? nextButtonContainerRef.current.offsetWidth
          : 0;
        const nextOffsetHeight = nextButtonContainerRef.current
          ? nextButtonContainerRef.current.offsetHeight
          : 0;
        // utility method to check if the element is closer to the end edge of the conveyorbelt
        const _shouldSnapToTheEndEdge = (
          ltr: boolean,
          orientation: 'horizontal' | 'vertical',
          elem?: HTMLElement | null,
          scroller?: HTMLDivElement | null
        ) => {
          if (!elem || !scroller) return false;
          let right;
          let left;
          let bottom;

          if (scroller === document.documentElement) {
            left = 0;
            right = document.documentElement.clientWidth;
            bottom = document.documentElement.clientHeight;
          } else {
            const scrollerBounds = scroller.getBoundingClientRect();
            right = canPaginateNext ? scrollerBounds.right - nextOffsetWidth : scrollerBounds.right;
            left = canPaginateNext ? scrollerBounds.left - nextOffsetWidth : scrollerBounds.left;
            bottom = canPaginateNext
              ? scrollerBounds.bottom - nextOffsetHeight
              : scrollerBounds.bottom;
          }
          const bounds = elem.getBoundingClientRect();
          return orientation === 'horizontal'
            ? ltr
              ? bounds.right > right
              : bounds.left < left
            : bounds.bottom > bottom;
        };

        // utility method to check that an element is fully visible inside conveyorbelt
        const _isElementInsideScrollerBounds = (
          orientation: 'vertical' | 'horizontal',
          elem?: HTMLElement | null,
          scroller?: HTMLDivElement | null
        ) => {
          if (!elem || !scroller) return false;
          let left;
          let right;
          let top;
          let bottom;
          if (scroller === document.documentElement) {
            left = 0;
            right = document.documentElement.clientWidth;
            top = 0;
            bottom = document.documentElement.clientHeight;
          } else {
            const scrollerBounds = scroller.getBoundingClientRect();
            left = canPaginatePrevious
              ? scrollerBounds.left + prevOffsetWidth
              : scrollerBounds.left;
            right = canPaginateNext ? scrollerBounds.right - nextOffsetWidth : scrollerBounds.right;
            top = canPaginatePrevious ? scrollerBounds.top + prevOffsetHeight : scrollerBounds.top;
            bottom = canPaginateNext
              ? scrollerBounds.bottom - nextOffsetHeight
              : scrollerBounds.bottom;
          }
          const bounds = elem.getBoundingClientRect();
          return orientation === 'horizontal'
            ? bounds.left <= right &&
                bounds.right <= right &&
                bounds.left >= left &&
                bounds.right >= left
            : bounds.top <= bottom &&
                bounds.bottom <= bottom &&
                bounds.top >= top &&
                bounds.bottom >= top;
        };

        const _isElementOverflowingScrollerBounds = (
          orientation: 'vertical' | 'horizontal',
          elem?: HTMLElement | null,
          scroller?: HTMLDivElement | null
        ) => {
          if (!elem || !scroller) return false;
          const bounds = elem.getBoundingClientRect();
          const scrollerBounds = scroller.getBoundingClientRect();
          let width = scrollerBounds.width;
          let height = scrollerBounds.height;
          if (orientation === 'horizontal') {
            width =
              canPaginatePrevious && prevButtonContainerRef.current
                ? width - prevButtonContainerRef.current.offsetWidth
                : width;
            width =
              canPaginateNext && nextButtonContainerRef.current
                ? width - nextButtonContainerRef.current.offsetWidth
                : width;
          } else {
            height =
              canPaginatePrevious && prevButtonContainerRef.current
                ? height - prevButtonContainerRef.current.offsetHeight!
                : height;
            height =
              canPaginateNext && nextButtonContainerRef.current
                ? height - nextButtonContainerRef.current.offsetHeight!
                : height;
          }

          return orientation === 'horizontal' ? bounds.width > width : bounds.height > height;
        };

        const isElementVisible = _isElementInsideScrollerBounds(
          orientation,
          element,
          overflowContainerRef.current
        );
        if (isElementVisible) {
          return;
        }
        const isElementOverflowing = _isElementOverflowingScrollerBounds(
          orientation,
          element,
          overflowContainerRef.current
        );

        if (!overflowContainerRef.current || !contentContainerRef.current) return;
        const shouldSnapEnd = _shouldSnapToTheEndEdge(
          direction === 'ltr',
          orientation,
          element,
          overflowContainerRef.current
        );
        if (orientation === 'vertical') {
          if (shouldSnapEnd && !isElementOverflowing) {
            overflowContainerRef.current.scrollTo({
              top:
                getOffsetTop(element, contentContainerRef.current) +
                element.offsetHeight -
                overflowContainerRef.current.offsetHeight +
                (nextButtonContainerRef.current?.offsetHeight
                  ? nextButtonContainerRef.current?.offsetHeight + 1
                  : 0),
              behavior: 'smooth'
            });
          } else {
            overflowContainerRef.current.scrollTo({
              top:
                getOffsetTop(element, contentContainerRef.current) -
                (prevButtonContainerRef.current?.offsetHeight
                  ? prevButtonContainerRef.current?.offsetHeight + 1
                  : 0),
              behavior: 'smooth'
            });
          }
          return;
        }

        if (direction === 'ltr') {
          if (shouldSnapEnd && !isElementOverflowing) {
            overflowContainerRef.current.scrollTo({
              left:
                getOffsetLeft(element, contentContainerRef.current) +
                element.offsetWidth -
                overflowContainerRef.current.offsetWidth +
                (nextButtonContainerRef.current?.offsetWidth
                  ? nextButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          } else {
            overflowContainerRef.current.scrollTo({
              left:
                getOffsetLeft(element, contentContainerRef.current) -
                (prevButtonContainerRef.current?.offsetWidth
                  ? prevButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          }
        } else {
          if (shouldSnapEnd && !isElementOverflowing) {
            overflowContainerRef.current.scrollTo({
              left:
                getOffsetLeft(element, contentContainerRef.current) -
                contentContainerRef.current.offsetWidth +
                overflowContainerRef.current.offsetWidth -
                (nextButtonContainerRef.current?.offsetWidth
                  ? nextButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          } else {
            overflowContainerRef.current.scrollTo({
              left:
                getOffsetLeft(element, contentContainerRef.current) +
                element.offsetWidth -
                contentContainerRef.current.offsetWidth +
                (prevButtonContainerRef.current?.offsetWidth
                  ? prevButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          }
        }
      },
      [canPaginateNext, canPaginatePrevious, direction, orientation]
    );

    const getNextInvisible = useCallback(() => {
      const nextInvisible = itemElementsRef.current.find((item) => {
        if (
          item.isVisible == false &&
          item.index >= 1 &&
          itemElementsRef.current[item.index - 1].isVisible == true
        ) {
          return true;
        }
        return false;
      });
      return nextInvisible?.element;
    }, [itemElementsRef]);

    const getPreviousInvisible = useCallback(() => {
      const previousInvisible = itemElementsRef.current.find((item) => {
        if (
          item.isVisible == false &&
          item.index < itemElementsRef.current.length - 1 &&
          itemElementsRef.current[item.index + 1].isVisible == true
        ) {
          return true;
        }
        return false;
      });
      return previousInvisible?.element;
    }, [itemElementsRef]);

    // paginates to the previous partially visible or hidden item in the conveyorbelt,
    // so that it is the last visible of the conveyorbelt view port
    const scrollToPreviousPage = useCallback(() => {
      const overflowContainer = overflowContainerRef.current;
      const scrollAmount =
        orientation === 'horizontal'
          ? overflowContainerRef.current?.clientWidth
          : overflowContainerRef.current?.clientHeight;

      if (overflowContainer && scrollAmount) {
        const previousInvisible: HTMLElement | undefined = getPreviousInvisible();
        if (orientation === 'vertical') {
          if (!previousInvisible) {
            overflowContainer.scrollTo({
              top: overflowContainer.scrollTop - scrollAmount,
              behavior: 'smooth'
            });
          } else {
            overflowContainer.scrollTo({
              top:
                getOffsetTop(previousInvisible, contentContainerRef.current) +
                previousInvisible.offsetHeight -
                overflowContainer.offsetHeight +
                (prevButtonContainerRef.current?.offsetHeight
                  ? prevButtonContainerRef.current?.offsetHeight + 1
                  : 0),
              behavior: 'smooth'
            });
          }
          return;
        }
        if (direction === 'ltr') {
          if (!previousInvisible) {
            overflowContainer.scrollTo({
              left: overflowContainer.scrollLeft - scrollAmount,
              behavior: 'smooth'
            });
          } else {
            overflowContainer.scrollTo({
              left:
                getOffsetLeft(previousInvisible, contentContainerRef.current) +
                previousInvisible.offsetWidth -
                overflowContainer.offsetWidth +
                (prevButtonContainerRef.current?.offsetWidth
                  ? prevButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          }
        } else {
          if (!previousInvisible) {
            overflowContainer.scrollTo({
              left: overflowContainer.scrollLeft + scrollAmount,
              behavior: 'smooth'
            });
          } else {
            overflowContainer.scrollTo({
              left:
                getOffsetLeft(previousInvisible, contentContainerRef.current) -
                (contentContainerRef.current ? contentContainerRef.current.offsetWidth : 0) +
                overflowContainer.offsetWidth -
                (prevButtonContainerRef.current?.offsetWidth
                  ? prevButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          }
        }
      }
    }, [direction, orientation, getPreviousInvisible]);

    // paginates to the next partially visible or hidden item in the conveyorbelt,
    // so that it is the first at the start of the conveyorbelt view port
    const scrollToNextPage = useCallback(() => {
      const overflowContainer = overflowContainerRef.current;
      const scrollAmount =
        orientation === 'horizontal'
          ? overflowContainerRef.current?.clientWidth
          : overflowContainerRef.current?.clientHeight;

      if (overflowContainer && scrollAmount) {
        const nextInvisible: HTMLElement | undefined = getNextInvisible();
        if (orientation === 'vertical') {
          if (!nextInvisible) {
            overflowContainer.scrollTo({
              top: overflowContainer.scrollTop + scrollAmount,
              behavior: 'smooth'
            });
          } else {
            overflowContainer.scrollTo({
              top:
                getOffsetTop(nextInvisible, contentContainerRef.current) -
                (nextButtonContainerRef.current?.offsetHeight
                  ? nextButtonContainerRef.current?.offsetHeight + 1
                  : 0),
              behavior: 'smooth'
            });
          }
          return;
        }
        if (direction === 'ltr') {
          if (!nextInvisible) {
            overflowContainer.scrollTo({
              left: overflowContainer.scrollLeft + scrollAmount,
              behavior: 'smooth'
            });
          } else {
            overflowContainer.scrollTo({
              left:
                (getOffsetLeft(nextInvisible, contentContainerRef.current) ?? 0) -
                (nextButtonContainerRef.current?.offsetWidth
                  ? nextButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          }
        } else {
          if (!nextInvisible) {
            overflowContainer.scrollTo({
              left: overflowContainer.scrollLeft - scrollAmount,
              behavior: 'smooth'
            });
          } else {
            overflowContainer.scrollTo({
              left:
                -(
                  (contentContainerRef.current ? contentContainerRef.current.offsetWidth : 0) -
                  (getOffsetLeft(nextInvisible, contentContainerRef.current) ?? 0)
                ) +
                (nextInvisible?.offsetWidth ?? 0) +
                (nextButtonContainerRef.current?.offsetWidth
                  ? nextButtonContainerRef.current?.offsetWidth + 1
                  : 0),
              behavior: 'smooth'
            });
          }
        }
      }
    }, [direction, orientation, getNextInvisible]);

    const setCurrentItem = useCallback(
      (node: HTMLElement | null) => {
        if (currentItemRef.current === node) return; // Already current, do nothing
        if (node) {
          currentItemRef.current = node;
          if (buttonsReady) {
            scrollIntoView(node);
          } else {
            pendingScrollRef.current = node;
          }
        }
      },
      [scrollIntoView, buttonsReady]
    );

    const onFocus = useCallback(
      (event: FocusEvent) => {
        if (baseRef.current?.isEqualNode(event.target as HTMLElement)) {
          return;
        }
        if (itemElementsRef.current.length > 0) {
          const item = itemElementsRef.current.find((item) =>
            item.element.contains(event.target as Node)
          );
          if (item) {
            scrollIntoView(item.element);
          }
        } else {
          scrollIntoView(event.target as HTMLElement);
        }
      },
      [scrollIntoView, itemElementsRef]
    );

    const previousButtonStyle = clsx([styles.buttonContainer, previousButtonClasses]);
    const nextButtonStyle = clsx([styles.buttonContainer, nextButtonClasses]);

    // Track when both button refs are available and set buttonsReady
    useEffect(() => {
      const prevReady = canPaginatePrevious ? !!prevButtonContainerRef.current : false;
      const nextReady = canPaginateNext ? !!nextButtonContainerRef.current : false;
      if (prevReady || nextReady) {
        setButtonsReady(true);
      }
    }, [canPaginateNext, canPaginatePrevious]);

    // When buttons become ready, trigger any pending scrollIntoView
    useEffect(() => {
      if (buttonsReady && pendingScrollRef.current) {
        scrollIntoView(pendingScrollRef.current);
        pendingScrollRef.current = null;
      }
    }, [buttonsReady, scrollIntoView]);

    const focusContext = useContext(ConveyorBeltFocusContext);
    const isInsideTabBar = !!focusContext;
    const isSuppressOverflowFocus = focusContext?.isSuppressOverflowFocus ?? false;

    // If, in backward navigation (Shift+Tab), the browser drops focus on overflow,
    // immediately put back to the tabbar.
    const onOverflowFocus = useCallback(() => {
      focusContext?.focusTabbarRoot();
    }, [focusContext]);

    const overflowTabIndexProps =
      // If inside tabbar context, only set tabIndex when suppressing overflow focus
      isInsideTabBar
        ? isSuppressOverflowFocus
          ? { tabIndex: -1 }
          : {}
        : // If not inside tabbar context, always set tabIndex to -1
          { tabIndex: -1 };

    return (
      <ConveyorBeltContext.Provider value={{ setCurrentItem, activeItemId, setActiveItemId }}>
        <div
          onFocus={onFocus}
          ref={baseRef}
          className={clsx([styles.conveyorStyle, variantClasses])}
          {...testIdProps}>
          {canPaginatePrevious && !buttonsHidden && (
            <div ref={prevButtonContainerRef} class={previousButtonStyle}>
              <BaseButton
                elementDetails={{ type: 'span', isFocusable: false }}
                styling={['min']}
                size={'sm'}
                variant={'ghost'}
                onAction={() => scrollToPreviousPage()}
                aria-hidden={true}
                testId={leftArrowTestIdProps}>
                <ButtonLabelLayout
                  size={'sm'}
                  display={'icons'}
                  startIcon={
                    orientation === 'horizontal' ? (
                      direction === 'ltr' ? (
                        <ChevronLeft />
                      ) : (
                        <ChevronRight />
                      )
                    ) : (
                      <ChevronUp />
                    )
                  }></ButtonLabelLayout>
              </BaseButton>
            </div>
          )}
          <div
            className={clsx([styles.overflowContainer, overflowClasses])}
            ref={overflowContainerRef}
            onFocus={onOverflowFocus}
            {...overflowTabIndexProps}>
            <div
              className={clsx([styles.contentContainer, variantClasses, contentClasses])}
              ref={contentContainerRef}>
              <div
                data-intersection-id="left"
                ref={leftRef}
                style={{ minWidth: 1, minHeight: 1 }}
              />
              {children}
              <div
                data-intersection-id="right"
                ref={rightRef}
                style={{ minWidth: 1, minHeight: 1 }}
              />
            </div>
          </div>
          {canPaginateNext && !buttonsHidden && (
            <div ref={nextButtonContainerRef} class={nextButtonStyle}>
              <BaseButton
                elementDetails={{ type: 'span', isFocusable: false }}
                styling={['min']}
                size={'sm'}
                variant={'ghost'}
                onAction={() => scrollToNextPage()}
                aria-hidden={true}
                testId={rightArrowTestIdProps}>
                <ButtonLabelLayout
                  size={'sm'}
                  display={'icons'}
                  startIcon={
                    orientation === 'horizontal' ? (
                      direction === 'ltr' ? (
                        <ChevronRight />
                      ) : (
                        <ChevronLeft />
                      )
                    ) : (
                      <ChevronDown />
                    )
                  }></ButtonLabelLayout>
              </BaseButton>
            </div>
          )}
        </div>
      </ConveyorBeltContext.Provider>
    );
  }
);
